// frontend/src/components/leads/LeadTable.js

import React, { useState } from "react";
import LeadStatusBadge from "./LeadStatusBadge";
import { formatUtcDateTimeParts } from "../../utils/timezone";

function LeadTable({
  leads,
  onBookDemo,
  onView,
  onEdit,
  onDelete,
  userRole,
  visibleColumns = {},
  productCatalog = [],
}) {
  const [overlay, setOverlay] = useState(null);

  if (!Array.isArray(leads) || leads.length === 0) {
    return (
      <div className="empty-state partner-empty-state">
        <i className="fas fa-inbox"></i>
        <h3>No leads found</h3>
        <p>Try changing your filters or add a new lead.</p>
      </div>
    );
  }

  const show = (column) => visibleColumns[column] !== false;
  const ownerCanSeeSalesRep = [
    "Admin",
    "SM",
    "Sales Owner",
    "Sales Manager",
  ].includes(String(userRole || "").trim());

  const normalizeList = (value) => {
    if (Array.isArray(value))
      return value.filter(
        (v) => v !== null && v !== undefined && String(v).trim(),
      );
    if (typeof value === "string" && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [value];
      } catch {
        return [value];
      }
    }
    return [];
  };

  const openOverlay = (title, items, lead) => {
    if (title === "Products" && lead) {
      setOverlay({
        title: "Products",
        items: items,
        lead: lead,
        type: "products",
      });
    } else {
      setOverlay({ title, items, type: "list" });
    }
  };

  const getProductCategory = (productName) => {
    if (!productCatalog || !Array.isArray(productCatalog)) return "";
    const found = productCatalog.find(
      (p) => p.name === productName || p.product_name === productName,
    );
    return found?.category || found?.category_name || "";
  };

  return (
    <>
      <div className="partner-table-wrapper">
        <table className="partner-leads-table">
          <thead>
            <tr>
              {show("dealership") && <th>Dealership</th>}
              {show("salesRep") && ownerCanSeeSalesRep && <th>Sales Rep</th>}
              {show("contact") && <th>Contact</th>}
              {show("status") && <th>Status</th>}
              {show("products") && <th>Products</th>}
              {show("dms") && <th>DMS Provider</th>}
              {show("demo") && <th>Demo</th>}
              <th className="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead) => {
              const products = normalizeList(lead.products);
              const dmsProviders = normalizeList(
                lead.dms_providers ?? lead.dmsProviders,
              );
              const otherDmsProviders = normalizeList(
                lead.other_dms_provider ?? lead.otherDmsProvider,
              );

              const combinedDmsProviders = [
                ...dmsProviders,
                ...otherDmsProviders.map((provider) => `Other: ${provider}`),
              ];
              const demoStatus =
                lead.demo_status || lead.demoStatus || "Not Booked";
              const demoDate = lead.demo_date || lead.demoDate || "";
              const demoTime = lead.demo_time || lead.demoTime || "";
              const demoDateTime =
                demoDate || demoTime
                  ? formatUtcDateTimeParts(demoDate, demoTime)
                  : "";
              const firstContact = Array.isArray(lead.contacts)
                ? lead.contacts[0]
                : null;
              const contactName =
                [
                  firstContact?.first_name || firstContact?.firstName,
                  firstContact?.last_name || firstContact?.lastName,
                ]
                  .filter(Boolean)
                  .join(" ") ||
                lead.contact_name ||
                "";
              const contactEmail =
                firstContact?.email || lead.contact_email || "";

              return (
                <tr key={lead.lead_id}>
                  {show("dealership") && (
                    <td data-label="Dealership" className="lead-company-cell">
                      <button
                        type="button"
                        className="partner-lead-link"
                        onClick={() => onView(lead.lead_id)}
                      >
                        {lead.company || "Unnamed Dealership"}
                      </button>
                      {lead.email && (
                        <div className="lead-secondary-text">{lead.email}</div>
                      )}
                    </td>
                  )}
                  {show("salesRep") && ownerCanSeeSalesRep && (
                    <td data-label="Sales Rep">
                      <div className="sales-rep-cell">
                        <span className="sales-rep-avatar">
                          {getInitials(lead.partner_name)}
                        </span>
                        <span>
                          {lead.partner_name || lead.sales_rep_name || "N/A"}
                        </span>
                      </div>
                    </td>
                  )}
                  {show("contact") && (
                    <td data-label="Contact">
                      {contactName ? (
                        <>
                          <div>{contactName}</div>
                          {contactEmail && (
                            <div className="lead-secondary-text">
                              {contactEmail}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="lead-secondary-text">N/A</span>
                      )}
                    </td>
                  )}
                  {show("status") && (
                    <td data-label="Status">
                      <LeadStatusBadge status={lead.status} />
                    </td>
                  )}
                  {show("products") && (
                    <td data-label="Products">
                      {products.length ? (
                        <div className="partner-table-tags">
                          {products.slice(0, 1).map((product, i) => (
                            <span
                              className="partner-table-tag lead-product-name"
                              key={`${product}-${i}`}
                              title={product}
                            >
                              {product}
                            </span>
                          ))}
                          {products.length > 1 && (
                            <button
                              type="button"
                              className="partner-table-more"
                              onClick={() =>
                                openOverlay("Products", products, lead)
                              }
                              title="View all products"
                            >
                              +{products.length - 1}
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="lead-secondary-text">None</span>
                      )}
                    </td>
                  )}
                  {show("dms") && (
                    <td data-label="DMS Provider">
                      {combinedDmsProviders.length ? (
                        <div className="partner-dms-list">
                          {combinedDmsProviders
                            .slice(0, 2)
                            .map((provider, i) => (
                              <span key={`${provider}-${i}`}>{provider}</span>
                            ))}
                          {combinedDmsProviders.length > 2 && (
                            <button
                              type="button"
                              className="partner-table-more"
                              onClick={() =>
                                openOverlay(
                                  "DMS Providers",
                                  combinedDmsProviders,
                                )
                              }
                              title="View all DMS providers"
                            >
                              +{combinedDmsProviders.length - 2}
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="lead-secondary-text">N/A</span>
                      )}
                    </td>
                  )}
                  {show("demo") && (
                    <td data-label="Demo">
                      <span
                        className={`demo-status-${String(demoStatus).toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {demoStatus}
                      </span>
                      {demoDateTime && (
                        <div className="lead-secondary-text">
                          {demoDateTime}
                        </div>
                      )}
                    </td>
                  )}
                  <td data-label="Actions" className="actions">
                    <div className="partner-row-actions">
                      <button
                        type="button"
                        className="partner-icon-action"
                        onClick={() => onView(lead.lead_id)}
                        title="View Lead"
                        aria-label="View Lead"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      <button
                        type="button"
                        className="partner-icon-action"
                        onClick={() => onEdit(lead.lead_id)}
                        title="Edit Lead"
                        aria-label="Edit Lead"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      {demoStatus === "Not Booked" && (
                        <button
                          type="button"
                          className="partner-icon-action success"
                          onClick={() => onBookDemo(lead.lead_id)}
                          title="Book Demo"
                          aria-label="Book Demo"
                        >
                          <i className="fas fa-calendar-plus"></i>
                        </button>
                      )}
                      {[
                        "Admin",
                        "SM",
                        "Sales Owner",
                        "Sales Manager",
                        "SR",
                        "Sales Representative",
                      ].includes(String(userRole || "")) && (
                        <button
                          type="button"
                          className="partner-icon-action danger"
                          onClick={() => onDelete(lead.lead_id)}
                          title="Delete Lead"
                          aria-label="Delete Lead"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ============================================================
          PRODUCTS MODAL - Like Partner Site
          ============================================================ */}
      {overlay && (
        <div
          className="modal-overlay"
          role="presentation"
          onMouseDown={() => setOverlay(null)}
        >
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={overlay.title}
            style={{
              maxWidth: overlay.type === "products" ? "480px" : "520px",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{overlay.title}</h3>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setOverlay(null)}
                aria-label="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              {overlay.type === "products" ? (
                <div className="products-modal-grid">
                  {overlay.items.map((item, i) => {
                    const category = getProductCategory(item);
                    return (
                      <div key={`${item}-${i}`} className="product-modal-item">
                        <span className="product-modal-name">{item}</span>
                        {category && (
                          <span className="product-modal-category">
                            {category}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <ul className="modal-list">
                  {overlay.items.map((item, i) => (
                    <li key={`${item}-${i}`}>{item}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setOverlay(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function getInitials(name) {
  if (!name) return "?";
  return String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

export default LeadTable;
